import {
    Field,
    useFormikContext,
} from 'formik';
import {useEffect} from 'react';
import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Anchor,
    Container,
    Fieldset,
    Loader,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import {
    FormikPasswordInput,
    FormikSelect,
    FormikSwitch,
    FormikTextInput,
} from '@flexinet/ui-components';

import {useDnsProvidersQuery, useFlexiPrincipalQuery} from '../hooks/useProviderConnectionsQuery';
import {ConnectFormValues} from './types';
import {CONNECT_PROVIDER_DOCS_URL} from '../config';
import {RoleBuilderPanel} from '../components/RoleBuilderPanel';
import {DEFAULT_GRANTS} from '../services/rolePolicyBuilder';

// FNP-1248 — the CONNECT form fields.
//
// ⚠️ THE CREDENTIAL FIELD SWITCHES ON THE CHOSEN PROVIDER'S `credentialType`.
//
// Rendering both a role-ARN field and a token field, and letting the tenant
// pick, would let them submit a token for Route 53 — which the adapter rejects
// at connect time. That is a failure the form can simply prevent by never
// offering the wrong field. The catalog declares what each provider REQUIRES;
// this reads it and renders exactly that one input.

export const FormComponent = () => {

    const {
        data: providers,
        isLoading,
        isError,
    } = useDnsProvidersQuery();

    // ⚠️ NOT GATED, AND ITS FAILURE IS NOT THE FORM'S FAILURE. The panel
    // renders guidance when the principal is absent, so a slow or failed
    // lookup leaves the tenant exactly the copy they had before FNP-1428
    // rather than blocking the one screen that must never fail.
    const {data: flexiPrincipal} = useFlexiPrincipalQuery();

    const {
        values,
        setFieldValue,
    } = useFormikContext<ConnectFormValues>();

    const selectedProvider = providers?.find(provider => provider.id === values.providerId);

    // ⚠️ THE DISCRIMINATOR IS MIRRORED INTO THE FORM STATE so the validation
    // schema can branch on it — Yup validates the values object, and it has no
    // access to the provider catalog.
    //
    // ⚠️ SWITCHING PROVIDER CLEARS BOTH CREDENTIAL FIELDS. Not just cosmetic: a
    // token typed for Cloudflare must never survive a switch to Route 53 and be
    // transmitted to the wrong vendor. The Yup schema strips the inapplicable
    // field as a second line of defence, but clearing here is what stops the
    // value from sitting in form state at all.
    useEffect(() => {
        const credentialType = selectedProvider?.credentialType ?? '';
        if (values.credentialType !== credentialType) {
            setFieldValue('credentialType', credentialType);
            setFieldValue('roleArn', '');
            setFieldValue('apiToken', '');
        }
    }, [selectedProvider?.credentialType, values.credentialType, setFieldValue]);

    return (
        <Container>
            <Fieldset
                legend={<Title order={3}><Text tt={'uppercase'}>{I18n.get('Provider')}</Text></Title>}
                px={'xl'}
                mb={'xl'}
            >
                {isLoading && <Loader size={'sm'}/>}

                {isError && (
                    <Alert color={'red'} title={I18n.get('Could not load the provider list')}>
                        {I18n.get('The list of DNS providers did not load, so there is nothing to connect to. Close this and try again.')}
                    </Alert>
                )}

                {!isLoading && !isError && (providers?.length ?? 0) === 0 && (
                    <Alert color={'yellow'} title={I18n.get('No providers available')}>
                        {productCopy('{product} has no DNS providers configured to connect to.')}
                    </Alert>
                )}

                {providers && providers.length > 0 && (
                    <>
                        <Field
                            name={'providerId'}
                            label={I18n.get('DNS provider')}
                            description={I18n.get('Which DNS host this connection is for.')}
                            component={FormikSelect}
                            withAsterisk
                            data={providers.map(provider => ({
                                value: provider.id,
                                // The catalog's human-readable name, never the id.
                                label: provider.name,
                            }))}
                        />

                        <Field
                            name={'displayName'}
                            label={I18n.get('Display name')}
                            description={I18n.get('Your own name for this connection, so several connections to the same provider are tellable apart — for example "Production AWS account".')}
                            component={FormikTextInput}
                            withAsterisk
                        />

                        {/* ─── ASSUMED_ROLE: the role ARN ─── */}
                        {selectedProvider?.credentialType === 'ASSUMED_ROLE' && (
                            <>
                                <Field
                                    name={'roleArn'}
                                    label={I18n.get('Role ARN')}
                                    description={productCopy('The IAM role in YOUR AWS account that {product} will assume.')}
                                    component={FormikTextInput}
                                    withAsterisk
                                    // ⚠️ THE EXAMPLE IS A RECOMMENDATION, WHETHER OR NOT IT IS
                                    // MEANT AS ONE. Most tenants will name their role whatever
                                    // this placeholder shows, so it carries the convention:
                                    // `FlexiNetwork-` clusters every Flexi integration role
                                    // together in an account holding thousands, `Domains` says
                                    // which product, and `IntegrationRole` tells someone who has
                                    // never read our documentation why it exists and whether they
                                    // may delete it. `flexi-email` and the testing platform will
                                    // add siblings under the same prefix.
                                    //
                                    // ⚠️ It is an EXAMPLE and never a requirement. A tenant names
                                    // roles in their own account by their own conventions, and
                                    // FNP-1361 is what happens when the platform quietly requires
                                    // a name it never asked for.
                                    placeholder={'arn:aws:iam::123456789012:role/FlexiNetwork-Domains-IntegrationRole'}
                                />

                                {/* ⚠️ SETS THE EXPECTATION BEFORE THE SUBMIT, NOT AFTER.
                                    A first connection CANNOT succeed: Flexi mints the
                                    external id from the connection id, so it does not
                                    exist until the connection does, and the tenant
                                    cannot have put it in their trust policy yet.
                                    Saying so here means the not-active result that
                                    follows reads as the next step rather than a
                                    failure the tenant has to diagnose. */}
                                <Alert
                                    color={'blue'}
                                    variant={'light'}
                                    title={I18n.get('This takes two steps')}
                                    mt={'md'}
                                >
                                    <Text size={'sm'}>
                                        {productCopy('When you save, {product} generates an external ID for this connection and shows it to you. Add that ID to your role\'s trust policy, then re-validate. The connection will not be active until you do — that is expected, not an error.')}
                                    </Text>
                                </Alert>

                                {/* ⚠️ FNP-1363 — THE ROLE IS BUILT HERE, NOT DESCRIBED.

                                    The customer ticks what they will grant and the
                                    policy assembles in front of them, ready to paste.
                                    This replaced a paragraph naming no Route 53 action
                                    at all, which the one person most likely to read it
                                    carefully — someone hesitating over handing a
                                    stranger their DNS — could not act on.

                                    ⚠️ The ticks also set the capability declaration
                                    (see `declarationFor`), so a connection cannot claim
                                    a permission its role was never granted. That is
                                    FNP-1357 closed by construction. */}
                                <RoleBuilderPanel
                                    // ⚠️ Falls back rather than trusting the field to
                                    // exist. This form is rendered by callers that
                                    // build their own initialValues, and a missing
                                    // key would otherwise take down the whole connect
                                    // form — the one screen that must never fail — over
                                    // an absent default.
                                    selection={values.grants ?? DEFAULT_GRANTS}
                                    onChange={grants => setFieldValue('grants', grants)}
                                    flexiPrincipal={flexiPrincipal?.arn}
                                />

                                {/* ⚠️ FNP-1276 — WHAT THE ROLE MUST BE ABLE TO DO,
                                    SAID WHILE THE TENANT IS STILL BUILDING IT.

                                    Decision 5 of docs/decisions/certificate-entity-model.md:
                                    the TENANT grants acm:RequestCertificate and
                                    acm:DeleteCertificate in this role. Flexi's own
                                    accounts hold no ACM write grant in any stage, so
                                    there is no Flexi-side fallback — a role without
                                    these actions authenticates perfectly and cannot
                                    issue a certificate.

                                    ⚠️ STATED HERE, AT ROLE-BUILDING TIME, NOT ONLY AT
                                    REQUEST TIME. The certificate request form already
                                    names the same two actions, but a tenant reading it
                                    has already built the role and has to go back and
                                    widen it. This is the moment the permission policy
                                    is being written.

                                    ⚠️ THE PERMISSIONS MUST STAY. ACM re-checks the
                                    validation record for the certificate's life, so
                                    both the Route 53 write grant and the ACM grants
                                    have to persist for years. A tenant who tightens
                                    the role months later breaks renewal, and nothing
                                    links the two events. That trap is why this alert
                                    says "keep" rather than only "grant". */}
                                <Alert
                                    color={'gray'}
                                    variant={'light'}
                                    title={I18n.get('Keep these permissions in place')}
                                    mt={'md'}
                                >
                                    <Stack gap={'xs'}>
                                        {/* ⚠️ FNP-1363 — THE ACTION LIST MOVED, THE
                                            RENEWAL WARNING DID NOT.

                                            The paragraph that used to sit here named
                                            acm:RequestCertificate and
                                            acm:DeleteCertificate in prose. The policy
                                            preview above now names every granted action
                                            literally, so repeating them here said the
                                            same thing twice on one screen — and worse,
                                            said it in a form the tenant cannot act on.

                                            What survives is the part the preview cannot
                                            express: that certificates are issued in the
                                            TENANT'S account, and that the grants must
                                            PERSIST. A policy preview shows what to grant
                                            now; it cannot warn about tightening the role
                                            months later. */}
                                        <Text size={'sm'}>
                                            {productCopy('Certificates are issued in your own AWS account, under this credential. {product} never holds certificate permissions of its own.')}
                                        </Text>
                                        <Text size={'sm'}>
                                            {I18n.get('Keep every one of these permissions in place after the certificate is issued. AWS re-checks the DNS validation record for the life of the certificate, so a role tightened months later fails renewal with nothing to connect it back to the change.')}
                                        </Text>
                                        {/* FNP-1301 — the same requirement as a
                                            page someone can read BEFORE opening
                                            this drawer, or send to whoever
                                            administers their AWS account. The
                                            copy above is reached only once a
                                            tenant has already started
                                            connecting. */}
                                        <Anchor
                                            size={'sm'}
                                            href={CONNECT_PROVIDER_DOCS_URL}
                                            target={'_blank'}
                                            rel={'noopener noreferrer'}
                                        >
                                            {I18n.get('The full trust policy and permission policy')}
                                        </Anchor>
                                    </Stack>
                                </Alert>
                            </>
                        )}

                        {/* ─── API_TOKEN: the token ─── */}
                        {selectedProvider?.credentialType === 'API_TOKEN' && (
                            <Field
                                name={'apiToken'}
                                label={I18n.get('API token')}
                                description={I18n.get('The token issued by your provider. It is stored encrypted and is never shown again after you save.')}
                                // ⚠️ A PASSWORD INPUT, so the token is masked as it is
                                // typed and cannot be read off a shoulder or a shared
                                // screen. It is also never rendered back anywhere in
                                // this module — no output type can carry it.
                                component={FormikPasswordInput}
                                withAsterisk
                            />
                        )}

                        {/* ─── FNP-1233: THE DECLARED WRITE CAPABILITY ───

                            ⚠️ WORDED AS A CLAIM ABOUT THE TENANT'S CREDENTIAL, NOT AS
                            A FLEXI PERMISSION. "This role can create and delete zones"
                            is something the tenant KNOWS and is telling us. "Enable
                            writes" would be a switch someone ticks hopefully, and a
                            hopeful tick produces a connection claiming a capability the
                            credential does not have — failing at the first write
                            instead of at the declaration.

                            ⚠️ DEFAULTS OFF, set in initialValues. A connection nobody
                            deliberately marked writable must not be able to write. */}
                        {/* ⚠️ FNP-1363 — ASSUMED_ROLE NO LONGER SHOWS THIS SWITCH,
                            and its absence is the fix rather than an omission.

                            On that path the declaration is DERIVED from the ticks in
                            RoleBuilderPanel, so the tenant declares by granting. A
                            separate switch beside the policy preview would be a second
                            place to state the same fact, and the two would drift — a
                            tenant ticking read-only while leaving the switch on is
                            exactly FNP-1357, which is live in dev right now.

                            API_TOKEN keeps it: there is no policy to build, so the
                            declaration has nowhere else to come from. */}
                        {selectedProvider?.credentialType === 'API_TOKEN' && (
                            <Field
                                name={'writable'}
                                label={I18n.get('This token can create and delete zones')}
                                description={productCopy('Leave this off if you granted read-only access. {product} will not attempt any write through this connection unless you turn it on, and you can change it later by re-validating.')}
                                component={FormikSwitch}
                                mt={'md'}
                            />
                        )}
                    </>
                )}
            </Fieldset>
        </Container>
    );
};
